import {all, call, put, take, select, takeLatest, putResolve} from 'redux-saga/effects';
import {toast} from 'react-toastify';
import {API} from '~/utils/request';
import {addStylesLink} from '~/utils/common';
import {setDictionary} from '~/utils/dictionary';
import {i18n} from '~/i18n';
import {
  setPagesTypes,
  getParsedCourseData,
} from '~/utils/course';

import {
  INITIALIZE_COURSE_PLAYER,
  INITIALIZE_SCORM,
  fetchTrackDataRequest,
  fetchTrackDataSuccess,
  fetchTrackDataFailure,
  fetchCourseDataRequest,
  fetchCourseDataSuccess,
  courseLastPage,
  fetchCourseDataFailure,
  enterLoadingPage,
  COURSE_ENTER_PAGE,
  courseEnterPage,
  courseEnterPageRequest,
  courseEnterPageSuccess,
  courseEnterPageFailure,
  COURSE_LEAVE_PAGE,
  COURSE_LEAVE_PAGE_SUCCESS,
  courseLeavePage,
  courseLeavePageRequest,
  courseLeavePageSuccess,
  courseLeavePageFailure,
  COURSE_ON_CONTINUE_CLICK,
  COURSE_ON_GO_BACK_CLICK,
  COURSE_ON_EXIT_CLICK,
  setActiveSlideId,
  courseEvent,
  setActiveCourse,
  courseSetUserData,
  setNextPageLoaded,
  courseSetCollectedData,
  addPageHistory, setCourseType,
} from '../actions/course.actions';

import {
  getNextPageId,
  createCollectedPageDataSelector,
  getSpeedCoursePagesCollectedData,
  getCollectedData,
  getEndPage,
  getEvents,
  getNextSlideId,
  getActivePageId,
  getPageHistory,
  getCourseData,
  getSlideId,
} from '../selectors/course.selectors';
import {postMessage} from '~/utils/dom';

import {
  getUserResult,
  getPageType,
  getMeetingType,
} from '~/stores/editor/selectors/course.selectors';
import {PAGE_ID_DESCRIPTION, PAGE_ID_FINISHED, PAGE_ID_LOADING, PAGE_ID_SPEED_COURSE, PageTypesEnum} from '~/utils/constants';
import {configGetConfig} from '../actions/config.actions';

function* initializeTrack(action) {
  const {sco} = action.payload;
  const scoData = yield call(sco.getSuspendData);

  yield put(fetchTrackDataRequest());
  yield put(setCourseType({courseType: 5, sco}));

  try {
    const data = yield call(API.getTrackData);

    console.log('sco', sco);
    console.log('data', data);
    data.tracks[0].dotts.forEach((dot, index) => {
      console.log(dot);
      console.log(Object.keys(scoData));
      if (Object.keys(scoData).includes(dot.id.toString())) {
        dot.status = 'DONE';
        if (index < data.tracks[0].dotts.length - 1 && data.tracks[0].dotts[index + 1].status === 'LOCKED') {
          data.tracks[0].dotts[index + 1].status = 'OPEN';
        }
      }
    });

    yield put(fetchTrackDataSuccess(data));
  } catch (error) {
    toast.error(i18n('error.no-content'));
    postMessage('TAB_COURSE_FAILED_TO_LOAD', 'IFRAME_COURSE_FAILED_TO_LOAD');
    console.error(error);

    yield put(fetchTrackDataFailure(error));
  }
}

function* initializeCoursePlayer(action) {
  const {courseId, token, scorm, sco, previewData} = action.payload;

  yield put(fetchCourseDataRequest());

  if (scorm) {
    yield put(setActiveCourse({activeCourse: courseId}));
    if (sco) {
      yield put(setCourseType({courseType: 5, sco}));
    }
  }

  try {
    if (token) {
      const data = yield call(API.useToken, token);
    }
    const [
      {courseData, stylesUrl, dictionary, userData},
      {data: {templateTypes}},
    ] = yield all([
      yield call(scorm && API.getScormData || API.getPlayerData, courseId),
      yield call(API.getTemplateTypes),
      yield put(configGetConfig()),
    ]);

    // The online player owns its UI strings locally (utils/dictionary.js); only
    // SCORM packages carry a baked dictionary (with sv/fi) that must be applied.
    if (scorm && dictionary) {
      setDictionary(dictionary);
    }
    setPagesTypes(templateTypes);
    if (stylesUrl !== undefined) {
      addStylesLink(stylesUrl); // If no url, no need to add.
    }

    if (userData) {
      if (userData.phce !== -1) {
        yield put(courseSetUserData(userData));
        API.setPhceId(userData.phce);
      }
      if (userData.pages) {
        yield put(courseSetCollectedData(userData.pages));
      }
    }

    if (courseData?.descriptionPage?.disabled) {
      yield put(enterLoadingPage());
      yield call(API.postEvent, 'leave', PAGE_ID_DESCRIPTION);
    }

    const parsedData = window.previewMode
      ? getParsedCourseData(previewData)
      : getParsedCourseData(courseData);

    if (parsedData) {
      yield put(fetchCourseDataSuccess(parsedData));
    }

    if (!scorm) {
      postMessage('TAB_COURSE_LOADED', 'IFRAME_COURSE_LOADED');
    }

    if (parsedData?.files?.length === 0) {
      if (window.previewMode) {
        return yield put(courseEnterPage({id: parsedData.pages?.[0]?.id}));
      }

      return yield put(courseEnterPage({id: yield select(getNextPageId)}));
    } else {
      return yield put(courseEnterPage({id: PAGE_ID_LOADING}));
    }
  } catch (error) {
    toast.error(i18n('error.no-content'));
    postMessage('TAB_COURSE_FAILED_TO_LOAD', 'IFRAME_COURSE_FAILED_TO_LOAD');
    console.error(error);

    return yield put(fetchCourseDataFailure(error));
  }
}

function* enterPage(action) {
  const {id} = action.payload;

  if (!id) {
    toast.error(i18n('error.no-content'));

    return;
  }

  console.log('enter-page', id);
  const activePageId = yield select(getActivePageId);

  if (activePageId === PAGE_ID_FINISHED) {
    console.log('should not go any more');

    return true;
  }

  yield put(courseEnterPageRequest({id}));

  try {
    // const response = yield API.postEvent('enter', id);
    // yield put(courseEnterPageSuccess(response));
    yield put(courseEnterPageSuccess());
  } catch (error) {
    toast.error('Failed to enter page.');
    yield put(courseEnterPageFailure());
    throw error;
  }
}

function* leavePage(action) {
  const {
    id,
    collectedData = {},
    clickedBack = false,
  } = action?.payload || {};

  const data = {...collectedData};

  if (collectedData?.questionnaire) {
    const allCollectedData = yield select(getCollectedData);

    data.speedCourseScore = Object.values(allCollectedData).reduce((acc, pageData) => {
      if (!pageData || !pageData.questionnaire) {
        return acc;
      }

      return acc + (pageData.questionnaire.points || 0);
    }, 0);
  }
  yield put(courseLeavePageRequest());

  const {sendType = 0, id: courseId} = yield select(getCourseData);

  yield putResolve(courseEvent({
    type: 'leave',
    pageId: id,
    data,
  }));

  try {
    const nextPageId = yield select(getNextPageId);
    const endPage = yield select(getEndPage);

    // for now different sendTypes 1 is not supported
    if (!clickedBack && nextPageId === endPage) {
      const events = yield select(getEvents);
      const response = yield API.postEvent('bulk', id, events);

      yield put(courseLeavePageSuccess(response.data));
    } else {
      yield put(courseLeavePageSuccess());
    }

    if (!clickedBack && nextPageId === PAGE_ID_FINISHED) {
      const result = yield select(getUserResult);

      yield put(courseLastPage());
      /* only send if its not scorm */
      if (sendType !== 5) {
        postMessage('TAB_COURSE_FINISHED', {type: 'IFRAME_COURSE_FINISHED', data: result});
      }
    }
  } catch (error) {
    toast.error('Failed to submit results.');
    yield put(courseLeavePageFailure());
    throw error;
  }
}

export function* onContinueClick({payload}) {
  const activePageId = yield select(getActivePageId);
  const activeSlideId = yield select(getSlideId);
  const pageType = yield select(getPageType);
  const isMeeting = yield select(getMeetingType);
  const nextPageId = payload?.pageId || (yield select(getNextPageId));

  const {auto} = payload && payload || {};

  if (isMeeting >= 1 && !auto) {
    console.log('STOP-STOP');

    return;
  }

  if (pageType !== PageTypesEnum.infographics) {
    if (activePageId === 'SPEED_COURSE') {
      const collectedData = yield select(getSpeedCoursePagesCollectedData);

      yield putResolve(courseLeavePage({
        id: PAGE_ID_SPEED_COURSE,
        collectedData,
      }));
    } else {
      const collectedData = yield select(createCollectedPageDataSelector(activePageId));

      yield putResolve(courseLeavePage({
        id: activePageId,
        collectedData,
      }));
    }
  }

  if (nextPageId !== activePageId) {
    yield putResolve(courseEnterPage({id: nextPageId}));
  }

  const nextSlideId = payload?.slideId || (yield select(getNextSlideId));

  if (nextSlideId) {
    yield put(setActiveSlideId(nextSlideId));
  }
  yield put(setNextPageLoaded(true));
  if (isMeeting >= 1) {
    postMessage('MEETING', {data: {pageId: nextPageId}});
  }

  yield put(addPageHistory({pageId: activePageId, slideId: activeSlideId}));
}

export function* onGoBackClick() {
  const {course: {data: {activePageId}}} = yield select();
  const pageType = yield select(getPageType);
  const pageHistory = yield select(getPageHistory);

  const {pageId, slideId} = pageHistory.pop() || {};

  if (!pageId) {
    return;
  }

  if (pageType !== PageTypesEnum.infographics) {
    const collectedData = yield select(createCollectedPageDataSelector(activePageId));

    yield put(courseLeavePage({
      id: activePageId,
      collectedData,
      clickedBack: true,
    }));
    yield take(COURSE_LEAVE_PAGE_SUCCESS);
  }

  yield put(courseEnterPage({id: pageId}));

  if (slideId) {
    yield put(setActiveSlideId(slideId));
  }
}

// eslint-disable-next-line require-yield
function* onExitClick() {
  postMessage('TAB_COURSE_CANCELLED', 'IFRAME_COURSE_CANCELLED');
}

export const sagas = [
  takeLatest(INITIALIZE_SCORM, initializeTrack),
  takeLatest(INITIALIZE_COURSE_PLAYER, initializeCoursePlayer),
  takeLatest(COURSE_ENTER_PAGE, enterPage),
  takeLatest(COURSE_LEAVE_PAGE, leavePage),
  takeLatest(COURSE_ON_CONTINUE_CLICK, onContinueClick),
  takeLatest(COURSE_ON_GO_BACK_CLICK, onGoBackClick),
  takeLatest(COURSE_ON_EXIT_CLICK, onExitClick),
];
